import axios from "axios";
import { EventEmitter } from "events";
import { createContext, useContext, useState } from "react";

const ApiKeyContext = createContext();

const ApiKeyEvents = new EventEmitter();

export const ApiKeyProvider = ({ children }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  const [searchParams, setSearchParams] = useState({
    clientId: "",
    environment: "",
    isRevoked: "",
  });

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchParams.clientId) params.clientId = searchParams.clientId;
      if (searchParams.environment) params.environment = searchParams.environment;
      if (searchParams.isRevoked !== "") params.isRevoked = searchParams.isRevoked;

      const response = await axios.get(`/api-keys`, { params });
      const { data, total, totalPages } = response.data;
      setApiKeys(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      console.error("Error fetching API keys:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async (clientId) => {
    try {
      const response = await axios.post(`/api-keys`, { clientId });
      ApiKeyEvents.emit("apiKey:created");
      return response.data;
    } catch (error) {
      console.error("Error creating API key:", error);
      throw error;
    }
  };

  const revokeApiKey = async (apiKeyId) => {
    try {
      await axios.delete(`/api-keys/${apiKeyId}`);
      ApiKeyEvents.emit("apiKey:revoked");
    } catch (error) {
      console.error("Error revoking API key:", error);
      throw error;
    }
  };

  const value = {
    apiKeys,
    loading,
    pagination,
    searchParams,
    fetchApiKeys,
    createApiKey,
    revokeApiKey,
    updatePagination,
    updateSearchParams,
    ApiKeyEvents,
  };

  return (
    <ApiKeyContext.Provider value={value}>{children}</ApiKeyContext.Provider>
  );
};

export const useApiKeyContext = () => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error("useApiKeyContext must be used within an ApiKeyProvider");
  }
  return context;
};
